import React, { useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';

import { StripeTipCheckoutSelection, StripeTipPaymentOption, TipAmount } from '../../types';
import ApruvTermsNotice from '../common/ApruvTermsNotice';
import PoweredByApruv from '../common/PoweredByApruv';
import {
  CompanyBrandProfile,
  brooksBrandProfile,
  getCompanyBrandCssVars,
} from '../../branding/companyBrand';
import {
  MIN_TIP_AMOUNT,
  centsToDollars,
  formatCurrency,
  getTipFeeBreakdown,
  isValidTipAmount,
} from '../../utils/tipFee';

interface PaymentOption {
  id: StripeTipPaymentOption;
  label: string;
  description: string;
  mark: string;
  icon: React.ElementType;
  tone: 'card' | 'cash';
}

interface StripeTipCheckoutProps {
  amounts: TipAmount[];
  brand?: CompanyBrandProfile;
  error?: string | null;
  header: string;
  loading?: boolean;
  onBack: () => void;
  onCheckout: (selection: StripeTipCheckoutSelection) => void;
  onNoThanks: () => void;
  subheader: string;
}

const paymentOptions: PaymentOption[] = [
  {
    id: 'card',
    label: 'Card or wallet',
    description: 'Secure Stripe checkout',
    mark: 'CARD',
    icon: CreditCardRoundedIcon,
    tone: 'card',
  },
  {
    id: 'cash_app',
    label: 'Cash App Pay',
    description: 'Pay through Cash App',
    mark: 'CASH',
    icon: AccountBalanceWalletRoundedIcon,
    tone: 'cash',
  },
];

const paymentMarkStyles: Record<PaymentOption['tone'], Record<string, string>> = {
  card: {
    backgroundColor: 'var(--color-gray-100)',
    color: 'var(--cx-brand-text)',
  },
  cash: {
    backgroundColor: 'var(--cx-brand-primary-soft)',
    color: 'var(--cx-brand-primary-dark)',
  },
};

function parseCustomAmount(value: string): number | null {
  const amount = Number.parseFloat(value);
  return isValidTipAmount(amount) ? amount : null;
}

const StripeTipCheckout: React.FC<StripeTipCheckoutProps> = ({
  amounts,
  brand = brooksBrandProfile,
  error,
  header,
  loading = false,
  onBack,
  onCheckout,
  onNoThanks,
  subheader,
}) => {
  const [selectedAmount, setSelectedAmount] = useState<TipAmount | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [coverProcessingFee, setCoverProcessingFee] = useState(false);
  const [paymentOption, setPaymentOption] = useState<StripeTipPaymentOption>('card');
  const hasCustomAmount = customAmount.trim() !== '';

  const resolvedAmount = useMemo(
    () => {
      if (hasCustomAmount) return parseCustomAmount(customAmount);
      const presetAmount = selectedAmount?.value;
      return isValidTipAmount(presetAmount) ? presetAmount : null;
    },
    [customAmount, hasCustomAmount, selectedAmount]
  );

  const canCheckout = isValidTipAmount(resolvedAmount) && !loading;
  const customAmountBelowMinimum = hasCustomAmount && !parseCustomAmount(customAmount);
  const feeBreakdown = useMemo(
    () => getTipFeeBreakdown(resolvedAmount ?? 0, coverProcessingFee),
    [coverProcessingFee, resolvedAmount]
  );
  const amountLabel = formatCurrency(feeBreakdown.amountCents);
  const processingFeeLabel = formatCurrency(feeBreakdown.processingFeeCents);
  const technicianTipLabel = formatCurrency(feeBreakdown.technicianTipCents);
  const totalLabel = formatCurrency(feeBreakdown.customerTotalCents);

  const handleAmountSelection = (amount: TipAmount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    setSelectedAmount(null);
  };

  const handleCheckout = () => {
    if (!resolvedAmount) return;
    onCheckout({
      amount: centsToDollars(feeBreakdown.amountCents),
      coverProcessingFee,
      paymentOption,
      processingFee: centsToDollars(feeBreakdown.processingFeeCents),
      technicianTip: centsToDollars(feeBreakdown.technicianTipCents),
      total: centsToDollars(feeBreakdown.customerTotalCents),
    });
  };

  return (
    <Box
      className="stripe-tip-checkout"
      style={getCompanyBrandCssVars(brand)}
      sx={{
        bgcolor: 'var(--cx-brand-bg)',
        color: 'var(--cx-brand-text)',
        minHeight: '100vh',
        pb: { xs: 28, sm: 30 },
        px: { xs: 1.5, sm: 3 },
        pt: { xs: 1.5, sm: 3 },
      }}
    >
      <Stack
        spacing={2}
        sx={{
          maxWidth: 460,
          minHeight: { xs: 'calc(100vh - 24px)', sm: 'calc(100vh - 48px)' },
          mx: 'auto',
        }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
          <IconButton
            aria-label="Back"
            onClick={onBack}
            sx={{
              bgcolor: 'var(--cx-brand-surface)',
              border: '1px solid',
              borderColor: 'var(--cx-brand-border)',
              height: 40,
              width: 40,
              '&:hover': { bgcolor: 'var(--color-gray-100)' },
            }}
          >
            <ArrowBackIosNewRoundedIcon sx={{ fontSize: 17 }} />
          </IconButton>

          <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, gap: 1, minWidth: 0 }}>
            <Avatar
              sx={{
                bgcolor: 'var(--cx-brand-primary-dark)',
                color: 'var(--color-base-white)',
                height: 32,
                width: 32,
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              {brand.initials}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {brand.companyName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--cx-brand-text-muted)' }} noWrap>
                Service completed
              </Typography>
            </Box>
          </Box>

          <Chip
            label="Stripe"
            size="small"
            sx={{
              bgcolor: 'var(--color-gray-100)',
              color: 'var(--cx-brand-text-muted)',
              fontWeight: 700,
            }}
          />
        </Box>

        <Card
          sx={{
            p: { xs: 2, sm: 2.5 },
            '--card-shadow': '0 0 2px 0 rgba(145, 158, 171, 0.20), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
          }}
        >
          <Stack spacing={2.25}>
            <Stack spacing={0.75}>
              <Typography
                variant="h4"
                sx={{
                  color: 'var(--cx-brand-text)',
                  fontSize: { xs: 24, sm: 26 },
                  lineHeight: 1.25,
                }}
              >
                {header}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--cx-brand-text-muted)', lineHeight: 1.6 }}>
                {subheader}
              </Typography>
            </Stack>

            <Divider />

            <Stack spacing={1.5}>
              <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Amount</Typography>
                <Chip
                  label={amountLabel}
                  size="small"
                  sx={{
                    bgcolor: resolvedAmount ? 'var(--cx-brand-primary-soft)' : 'var(--color-gray-200)',
                    color: resolvedAmount ? 'var(--cx-brand-primary-dark)' : 'var(--cx-brand-text)',
                    fontWeight: 700,
                  }}
                />
              </Box>

              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                {amounts.map((amount) => {
                  const selected = selectedAmount?.value === amount.value;

                  return (
                    <Button
                      color={selected ? 'primary' : 'inherit'}
                      key={amount.value}
                      onClick={() => handleAmountSelection(amount)}
                      sx={{
                        bgcolor: selected ? 'var(--cx-brand-primary-soft)' : 'var(--color-gray-100)',
                        border: '1px solid',
                        borderColor: selected ? 'var(--cx-brand-primary)' : 'transparent',
                        color: selected ? 'var(--cx-brand-primary-dark)' : 'var(--cx-brand-text)',
                        fontSize: 18,
                        fontWeight: 800,
                        height: 58,
                        minHeight: 58,
                        '&:hover': {
                          bgcolor: selected ? 'var(--cx-brand-primary-soft)' : 'var(--color-gray-200)',
                          borderColor: selected ? 'var(--cx-brand-primary)' : 'var(--cx-brand-border)',
                        },
                      }}
                      type="button"
                      variant="outlined"
                    >
                      {amount.name}
                    </Button>
                  );
                })}
              </Box>

              <TextField
                fullWidth
                error={customAmountBelowMinimum}
                helperText={customAmountBelowMinimum ? 'Minimum is $1.00' : undefined}
                label="Custom amount"
                onChange={(event) => handleCustomAmountChange(event.target.value)}
                placeholder="Enter amount"
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  },
                  htmlInput: {
                    inputMode: 'decimal',
                    min: MIN_TIP_AMOUNT,
                    step: '0.01',
                  },
                }}
                type="number"
                value={customAmount}
              />

              <Box
                sx={{
                  bgcolor: 'var(--color-gray-50)',
                  border: '1px solid',
                  borderColor: 'var(--cx-brand-border)',
                  borderRadius: 2,
                  p: 1.25,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={coverProcessingFee}
                      onChange={(event) => setCoverProcessingFee(event.target.checked)}
                      sx={{
                        color: 'var(--color-gray-500)',
                        '&.Mui-checked': { color: 'var(--cx-brand-primary)' },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: 'var(--cx-brand-text)', fontWeight: 800 }}>
                      Cover payment & payout fees
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />

                <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: 'var(--cx-brand-text-muted)' }}>
                      Payment & payout fees
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--cx-brand-text)', fontWeight: 800 }}>
                      {processingFeeLabel}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: 'var(--cx-brand-text-muted)' }}>
                      Technician receives
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--cx-brand-text)', fontWeight: 800 }}>
                      {technicianTipLabel}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Stack>
        </Card>

        <Card
          sx={{
            p: { xs: 2, sm: 2.5 },
            '--card-shadow': '0 0 2px 0 rgba(145, 158, 171, 0.20), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
          }}
        >
          <Stack spacing={1.5}>
            <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Box>
                <Typography variant="subtitle2">Payment method</Typography>
                <Typography variant="caption" sx={{ color: 'var(--cx-brand-text-muted)' }}>
                  Redirects to Stripe checkout
                </Typography>
              </Box>
              <AccountBalanceWalletRoundedIcon sx={{ color: 'text.disabled' }} />
            </Box>

            <Stack spacing={1}>
              {paymentOptions.map((option) => {
                const selected = paymentOption === option.id;
                const OptionIcon = option.icon;

                return (
                  <Button
                    color="inherit"
                    key={option.id}
                    onClick={() => setPaymentOption(option.id)}
                    sx={{
                      bgcolor: selected ? 'var(--cx-brand-primary-soft)' : 'var(--cx-brand-surface)',
                      border: '1px solid',
                      borderColor: selected ? 'var(--cx-brand-primary)' : 'var(--cx-brand-border)',
                      boxShadow: selected ? '0 0 0 3px color-mix(in srgb, var(--cx-brand-primary) 16%, transparent)' : 'none',
                      justifyContent: 'flex-start',
                      minHeight: 68,
                      p: 1.25,
                      '&:hover': {
                        bgcolor: selected ? 'var(--cx-brand-primary-soft)' : 'var(--color-gray-50)',
                        borderColor: selected ? 'var(--cx-brand-primary)' : 'var(--color-gray-300)',
                      },
                    }}
                    type="button"
                    variant="outlined"
                  >
                    <Box
                      sx={{
                        alignItems: 'center',
                        borderRadius: 1,
                        display: 'flex',
                        flexShrink: 0,
                        fontSize: 10,
                        fontWeight: 900,
                        height: 40,
                        justifyContent: 'center',
                        mr: 1.5,
                        width: 62,
                        ...paymentMarkStyles[option.tone],
                      }}
                    >
                      {option.mark}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                      <Typography variant="subtitle2">{option.label}</Typography>
                      <Typography variant="caption" sx={{ color: 'var(--cx-brand-text-muted)', display: 'block' }}>
                        {option.description}
                      </Typography>
                    </Box>

                    <OptionIcon sx={{ color: 'text.disabled', mr: 0.75 }} />
                    <Radio
                      checked={selected}
                      sx={{
                        color: 'var(--color-gray-500)',
                        p: 0.5,
                        '&.Mui-checked': { color: 'var(--cx-brand-primary)' },
                      }}
                    />
                  </Button>
                );
              })}
            </Stack>
          </Stack>
        </Card>

        {error ? (
          <Card
            sx={{
              p: 1.5,
              bgcolor: 'var(--color-error-50)',
              border: '1px solid var(--color-error-200)',
              boxShadow: 'none',
            }}
          >
            <Typography variant="body2" color="error.dark" sx={{ fontWeight: 700 }}>
              {error}
            </Typography>
          </Card>
        ) : null}

        <Box sx={{ flexGrow: 1 }} />
      </Stack>

      <Box
        sx={{
          bottom: { xs: 'max(12px, env(safe-area-inset-bottom))', sm: 24 },
          left: 0,
          pointerEvents: 'none',
          position: 'fixed',
          px: { xs: 1.5, sm: 3 },
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Card
          sx={{
            boxShadow: '0 -8px 24px rgba(145, 158, 171, 0.14)',
            maxWidth: 460,
            mx: 'auto',
            p: 2,
            pointerEvents: 'auto',
          }}
        >
          <Stack spacing={1.5}>
            <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                Total
              </Typography>
              <Typography variant="h4" sx={{ fontSize: 24, color: 'var(--cx-brand-text)' }}>
                {totalLabel}
              </Typography>
            </Box>

            <ApruvTermsNotice />

            <Button
              disabled={!canCheckout}
              endIcon={<ArrowForwardRoundedIcon />}
              fullWidth
              onClick={handleCheckout}
              type="button"
              variant="contained"
              sx={{
                bgcolor: 'var(--cx-brand-primary)',
                color: 'var(--color-base-white)',
                '&:hover': { bgcolor: 'var(--cx-brand-primary-dark)' },
              }}
            >
              {loading ? 'Starting checkout...' : 'Continue to checkout'}
            </Button>
            <Button color="inherit" fullWidth onClick={onNoThanks} type="button" variant="text">
              No thanks
            </Button>
            <PoweredByApruv compact sx={{ pb: 0 }} />
          </Stack>
        </Card>
      </Box>
    </Box>
  );
};

export default StripeTipCheckout;
